import PageBreadcrumb from "../../components/common/CustomBreadCrumb";
import PageMeta from "../../components/common/PageMeta";
import ComponentCard from "../../components/common/ComponentCard";
import Label from "../../components/form/Label";
import Input from "../../components/form/input/InputField";
import Select from "../../components/form/Select";
import TextArea from "../../components/form/input/TextArea";
import Radio from "../../components/form/input/Radio";
import { useEffect, useState, useRef } from "react";
import useGoBack from "../../hooks/useGoBack";
import FlashcardService from "../../services/flashcard.service";
import { toast } from "react-toastify";
import { useParams } from "react-router";
import { getImageDimensions, isValidURL } from "../../utils/validation";
import AchievementService from "@/services/achievement.service";
import { useTranslation } from "react-i18next";

const AchievementType = [
  { label: 'Streak', value: 'Streak' },
  { label: 'Leaderboard', value: 'Leaderboard' }
]

const OperatorType = [
  { label: '>=', value: '>=' },
  { label: '<=', value: '<=' },
  { label: '=', value: '=' }
]

export default function EditAchievement() {
  const { achievement_id } = useParams();
  const [icon, setIcon] = useState<string>("");
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [achievementData, setAchievementData]: any = useState({
    name: "",
    description: "",
    type: "",
    icon: "",
    condition: {
      field: "",
      operator: "",
      value: 0
    },
    rewardPoints: 0
  });

  const [errors, setErrors] = useState({
    name: "",
    description: "",
    type: "",
    icon: "",
    condition: "",
    rewardPoints: ""
  });

  const [selectedUrlOrUpload, setSelectedUrlOrUpload] = useState({
    icon: "URL"
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const goBack = useGoBack();
  const { t } = useTranslation();

  const validate = () => {
    const newErrors = {
      name: "",
      description: "",
      type: "",
      icon: "",
      condition: "",
      rewardPoints: ""
    };

    if (achievementData.name.trim() === "") {
      newErrors.name = t("Name is required");
    }
    if (achievementData.description.trim() === "") {
      newErrors.description = t("Description is required");
    }
    if (achievementData.type.trim() === "") {
      newErrors.type = t("Type is required");
    }
    if (typeof achievementData.icon !== "object") {
      if (achievementData.icon.trim() === "" && icon.trim() === "") {
        newErrors.icon = t("Icon is required");
      }
    }
    if (achievementData.condition.field.trim() === "" || achievementData.condition.operator.trim() === "" || achievementData.condition.value <= 0) {
      newErrors.condition = t("Condition is required");
    }
    if (achievementData.rewardPoints <= 0) {
      newErrors.rewardPoints = t("Reward points must be greater than 0");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).some(key => newErrors[key as keyof typeof newErrors] !== "");
  };


  const handleRadioChangeIcon = async (value: string) => {
    setSelectedUrlOrUpload({
      ...selectedUrlOrUpload,
      icon: value
    });
    if (value === "URL") {
      if (isValidURL(achievementData.icon)) {
        setIcon(achievementData.icon ? achievementData.icon : "");
      } else {
        setIcon("");
      }
    } else {
      if (!isValidURL(achievementData.icon)) {
        setIcon(achievementData.icon ? await fetchSignedUrl(achievementData.icon) : "");
      } else {
        setIcon("");
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const fetchAchievement = async () => {
    try {
      const response = await AchievementService.getAchievementById(achievement_id || "");
      if (response.data) {
        setAchievementData({
          name: response.data.name,
          description: response.data.description,
          type: response.data.type,
          icon: response.data.icon,
          condition: response.data.condition,
          rewardPoints: response.data.rewardPoints
        })
        if (response.data.icon) {
          if (isValidURL(response.data.icon)) {
            setIcon(response.data.icon);
          } else {
            setIcon(await fetchSignedUrl(response.data.icon));
          }
        }
        setSelectedUrlOrUpload({
          icon: isValidURL(response.data.icon) ? "URL" : "Upload",
        });
      }
    } catch (err) {
      console.error("Error fetching decks:", err);
    }
  };

  const fetchSignedUrl = async (fileKey: string): Promise<any> => {
    try {
      const res = await FlashcardService.getFile({ fileKey });
      if (res?.data?.status == 200) {
        return res?.data?.fileUrl;
      }
    } catch (err) {
      console.error("Error fetching signed URL:", err);
      return '';
    }
  };

  useEffect(() => {
    fetchAchievement()
  }, []);

  const handleEditAchievement = () => {
    setIsLoading(true);
    const fd = new FormData();
    for (let key of Object.keys(achievementData)) {
      if (key === "condition") {
        fd.append(key, JSON.stringify(achievementData[key]));
      } else {
        fd.append(key, achievementData[key]);
      }
    }
    fd.append("_id", achievement_id || "");
    AchievementService.editAchievement(fd)
      .then((data: any) => {
        if (data.data.status === 200) {
          toast.success("Edit Successfully!");
          setTimeout(() => {
            window.location.href = "/list-achievements";
          }, 1500);
        } else {
          toast.error("Edit Failed!");
          setIsLoading(false);
        }
      })
      .catch((errors) => {
        setIsLoading(false);
        toast.error("Edit Failed!");
        console.log(errors);
      });
  };

  const simulateProgress = (setProgress: (progress: number) => void, onComplete: () => void) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      if (progress >= 100) {
        clearInterval(interval);
        onComplete();
      } else {
        setProgress(progress);
      }
    }, 50);
  };

  return (
    <div>
      <PageMeta
        title="VIYUME - VIYUME Dashboard"
        description=""
      />
      <div className="mx-auto max-w-[80%] grid grid-cols-1 gap-6">
        <div className="space-y-6">
          <ComponentCard title="">
            <div className="space-y-6">
              <div>
                <Label>{t('Name')}</Label>
                <Input
                  type="text"
                  value={achievementData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAchievementData({ ...achievementData, name: e.target.value })}
                  placeholder={t("Please enter name")}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>
              <div>
                <Label>{t('Description')}</Label>
                <TextArea
                  value={achievementData.description}
                  onChange={(value) => setAchievementData({ ...achievementData, description: value })}
                  rows={6}
                  placeholder={t("Please enter description")}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>
              {achievementData.type && <div>
                <Label>{t('Type')}</Label>
                <Select
                  options={AchievementType}
                  defaultValue={achievementData.type}
                  placeholder={t("Select an option")}
                  onChange={(value: string) => {
                    setAchievementData({
                      ...achievementData,
                      type: value,
                    })
                  }}
                  className="dark:bg-dark-900"
                />
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                )}
              </div>}

              <div>
                <Label>{t('Icon')}</Label>
                <div className="flex flex-wrap items-center gap-8">
                  <Radio
                    id="radio3"
                    name="choiceImage"
                    value="URL"
                    checked={selectedUrlOrUpload.icon === "URL"}
                    onChange={handleRadioChangeIcon}
                    label="URL"
                  />
                  <Radio
                    id="radio4"
                    name="choiceImage"
                    value="Upload"
                    checked={selectedUrlOrUpload.icon === "Upload"}
                    onChange={handleRadioChangeIcon}
                    label="Upload"
                  />
                </div>
                {selectedUrlOrUpload.icon === "Upload" ?
                  <>
                    <div className="flex flex-col space-y-2 mt-5 max-w-xs">
                      <div className="relative">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
                              if (!validImageTypes.includes(file.type)) {
                                setErrors(prev => ({
                                  ...prev,
                                  icon: 'Please select a valid icon file (JPEG, PNG, GIF, WEBP)'
                                }));
                                setIcon("");
                                setAchievementData({
                                  ...achievementData,
                                  icon: "",
                                })
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                                return;
                              }

                              if (file.size > 500 * 1024) {
                                setErrors(prev => ({
                                  ...prev,
                                  icon: 'File size must be less than 500KB'
                                }));
                                setIcon("");
                                setAchievementData({
                                  ...achievementData,
                                  icon: "",
                                })
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                                return;
                              }

                              // Validate image dimensions
                              try {
                                const dimensions: any = await getImageDimensions(file);
                                if (dimensions) {
                                  if (dimensions.width < 100 || dimensions.width > 200 || dimensions.height < 100 || dimensions.height > 200) {
                                    setErrors(prev => ({
                                      ...prev,
                                      icon: 'Image dimensions must be between 100x100px and 200x200px'
                                    }));
                                    setIcon("");
                                    setAchievementData({
                                      ...achievementData,
                                      icon: "",
                                    })
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = "";
                                    }
                                    return;
                                  }
                                }
                              } catch (error) {
                                setErrors(prev => ({
                                  ...prev,
                                  icon: 'Failed to read image dimensions'
                                }));
                                setIcon("");
                                setAchievementData({
                                  ...achievementData,
                                  icon: "",
                                })
                                if (fileInputRef.current) {
                                  fileInputRef.current.value = "";
                                }
                                return;
                              }

                              setErrors(prev => ({
                                ...prev,
                                icon: ""
                              }));

                              setUploadProgress(0);
                              const reader = new FileReader();

                              simulateProgress(setUploadProgress, () => {
                                reader.readAsDataURL(file);
                              });

                              reader.onload = (event) => {
                                if (event.target?.result) {
                                  const base64String = event.target.result.toString();
                                  setUploadProgress(100);
                                  setIcon(base64String);
                                  setTimeout(() => {
                                    setUploadProgress(0);
                                    setAchievementData({
                                      ...achievementData,
                                      icon: file,
                                    })
                                  }, 1000);
                                }
                              };

                              reader.onerror = () => {
                                toast.error('Error reading file');
                                setUploadProgress(0);
                              };

                            }
                          }}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                          id="icon-upload"
                        />
                        <label
                          htmlFor="icon-upload"
                          className="flex items-center justify-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer font-medium"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                          </svg>
                          {t('Upload Icon')}
                        </label>
                      </div>
                    </div>


                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-2">
                        <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                          <div
                            className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{uploadProgress}% uploaded</p>
                      </div>
                    )}

                    {icon && (
                      <div className="mt-4 relative inline-flex items-center gap-2">
                        <img
                          src={icon}
                          alt="Preview"
                          className="max-w-xs rounded-lg shadow-lg"
                          onClick={() => setIsModalOpen(true)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setIcon("");
                            setAchievementData({
                              ...achievementData,
                              icon: "",
                            });
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}

                  </> :
                  <>
                    <Input
                      type="text"
                      value={achievementData.icon}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setAchievementData({ ...achievementData, icon: e.target.value })
                        setIcon(e.target.value)
                      }}
                      placeholder="Please enter image URL"
                      className="mt-2"
                    />
                    {icon && (
                      <div className="mt-4 relative inline-flex items-center gap-2">
                        <img
                          src={icon}
                          alt="Preview"
                          className="max-w-xs rounded-lg shadow-lg"
                          onClick={() => setIsModalOpen(true)}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setIcon("");
                            setAchievementData({
                              ...achievementData,
                              icon: "",
                            });
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                          className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600 focus:outline-none"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </>
                }
                {errors.icon && (
                  <p className="mt-1 text-sm text-red-500">{errors.icon}</p>
                )}
              </div>
              <div className="mt-4">
                <Label className="mb-2 block"> {t('Condition')}</Label>
                <div className="flex gap-4">
                  <Input
                    type="text"
                    placeholder="Field"
                    value={achievementData.condition.field}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAchievementData({ ...achievementData, condition: { ...achievementData.condition, field: e.target.value } })}
                  />
                  {/* <Input
                    type="text"
                    placeholder="Operator"
                    value={achievementData.condition.operator}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAchievementData({ ...achievementData, condition: { ...achievementData.condition, operator: e.target.value } })}
                  /> */}
                  {achievementData.condition.operator && <Select
                    options={OperatorType}
                    defaultValue={achievementData.condition.operator}
                    placeholder={t('Select operator')}
                    onChange={(value: string) => {
                      setAchievementData({ ...achievementData, condition: { ...achievementData.condition, operator: value } })
                    }}
                    className="dark:bg-dark-900"
                  />}

                  <Input
                    type="number"
                    placeholder="Value"
                    value={achievementData.condition.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAchievementData({ ...achievementData, condition: { ...achievementData.condition, value: Number(e.target.value) } })}
                  />
                </div>
                {errors.condition && (
                  <p className="mt-1 text-sm text-red-600">{errors.condition}</p>
                )}
              </div>
              <div>
                <Label>{t('Reward Points')}</Label>
                <Input
                  type="number"
                  value={achievementData.rewardPoints}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAchievementData({ ...achievementData, rewardPoints: Number(e.target.value) })}
                  placeholder={t('Please enter reward points')}
                  min="1"
                />
                {errors.rewardPoints && (
                  <p className="mt-1 text-sm text-red-600">{errors.rewardPoints}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={goBack}
              >
                {t('Cancel')}
              </button>
              <div className="mx-2"></div>
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={() => {
                  if (!isLoading) {
                    const hasErrors = validate();
                    if (!hasErrors) {
                      handleEditAchievement()
                    } else {
                      toast.warning("Validation failed - Please check your inputs");
                    }
                  }
                }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('Editing')}...
                  </div>
                ) : (
                  <> {t('Edit')}</>
                )}
              </button>
            </div>
            {isModalOpen && (
              <>
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl p-4 relative m-4">
                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="absolute -top-2 -right-2 z-50 bg-gray-800 text-white hover:bg-gray-900 w-8 h-8 rounded-full flex items-center justify-center text-lg transition-colors"
                    >
                      &times;
                    </button>
                    <div className="h-[80vh] flex items-center justify-center">
                      <img
                        src={icon}
                        alt="Preview"
                        className="max-h-full max-w-full object-contain rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </ComponentCard>
        </div>
      </div>
    </div>
  );
}
